import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { CloseOutlined, FilterListOutlined } from '@mui/icons-material'
import { Button, Divider, FormControl, FormControlLabel, FormLabel, Grid, Menu, Radio, RadioGroup, Switch, ToggleButton } from '@mui/material'

import useStyles from '../index.styles'

const severityMatrix: Record<string, string> = {
  all: 'All',
  urgent: 'Urgent',
  warning: 'Warning',
  info: 'Informational',
}

export const defaultAlertFilters = { severity: 'all' as const, respondedOnly: false }

const FilterComponent = ({ setAlertFilters }: { setAlertFilters: Dispatch<SetStateAction<DeviceAlertFilterForm>> }) => {
  const classes = useStyles

  const { deviceId } = useParams()
  const [searchParams] = useSearchParams()
  const tab = searchParams.get('tab') || 'alerts'

  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null)

  const [severityPending, setSeverityPending] = useState<DeviceAlertFilterForm['severity']>(defaultAlertFilters.severity)
  const [respondedOnlyPending, setRespondedOnlyPending] = useState<boolean>(defaultAlertFilters.respondedOnly)

  const filtersAreDefault = severityPending === defaultAlertFilters.severity && respondedOnlyPending === defaultAlertFilters.respondedOnly

  // Clear filter when the device id changes
  useEffect(() => {
    setAlertFilters({ ...defaultAlertFilters })
  }, [deviceId, setAlertFilters])

  // clear filter if user changes tabs
  useEffect(() => {
    setAlertFilters({ ...defaultAlertFilters })
  }, [tab, setAlertFilters])

  const handleMenuClose = () => {
    setFilterMenuAnchor(null)
  }

  return (
    <>
      <ToggleButton
        selected={Boolean(filterMenuAnchor)}
        value="check"
        size="small"
        onClick={(e) => {
          // If filters  are not default, reset them
          if (!filtersAreDefault) {
            // Reset filters
            setSeverityPending(defaultAlertFilters.severity)
            setRespondedOnlyPending(defaultAlertFilters.respondedOnly)
            setAlertFilters({ ...defaultAlertFilters })
          } else {
            // Otherwise, open the filter dropdown
            setFilterMenuAnchor(e.currentTarget)
          }
        }}
        aria-label={filterMenuAnchor || filtersAreDefault ? 'Filter alerts' : 'Reset filters'}
        sx={classes.filterButton}
      >
        {filterMenuAnchor || filtersAreDefault ? <FilterListOutlined /> : <CloseOutlined color="primary" />}
      </ToggleButton>
      <Menu
        id="filter-menu"
        open={Boolean(filterMenuAnchor)}
        anchorEl={filterMenuAnchor}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          list: { sx: classes.filterMenuPaper },
        }}
      >
        <Grid
          container={true}
          sx={{
            width: 350,
          }}
        >
          <Grid
            container={true}
            spacing={2}
            size={12}
            sx={{
              p: 2,
            }}
          >
            <Grid size={12}>
              <FormControl>
                <FormLabel>Severity</FormLabel>
                <RadioGroup name="severity" defaultValue="all" sx={{ p: 1 }}>
                  {['all', 'urgent', 'warning', 'info'].map((value) => (
                    <FormControlLabel
                      key={value}
                      control={<Radio checked={severityPending === value} />}
                      onChange={() => setSeverityPending(value as DeviceAlertFilterForm['severity'])}
                      value={value}
                      label={severityMatrix[value]}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl>
                <FormLabel>Display Only</FormLabel>
                <FormControlLabel
                  sx={{ p: 1 }}
                  control={<Switch value={respondedOnlyPending} onClick={() => setRespondedOnlyPending((old) => !old)} />}
                  label="Acknowledged Alerts"
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Divider />
          </Grid>
          <Grid
            container={true}
            sx={{
              gap: '4px',
              p: 2,
            }}
          >
            <Button
              variant="contained"
              disabled={filtersAreDefault}
              onClick={() => {
                setAlertFilters({ severity: severityPending, respondedOnly: respondedOnlyPending })
                setFilterMenuAnchor(null)
              }}
            >
              Apply
            </Button>
            <Button
              disabled={filtersAreDefault}
              onClick={() => {
                setAlertFilters({ ...defaultAlertFilters })
                setSeverityPending(defaultAlertFilters.severity)
                setRespondedOnlyPending(defaultAlertFilters.respondedOnly)
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Menu>
    </>
  )
}

export default FilterComponent
